import { invokeFunction } from "@/lib/api";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import type { Department, Role, StaffLevel } from "@/features/auth/types";

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  staffLevel: StaffLevel | null;
  department: Department | null;
  dispatcherId: string | null;
  quickmanageCustomerId: string;
  samsaraDriverId: string;
  carrierId: string;
  createdAt: string;
}

interface ProfileRow {
  id: string;
  email: string | null;
  full_name: string | null;
  role: Role;
  staff_level: StaffLevel | null;
  department: Department | null;
  dispatcher_id: string | null;
  quickmanage_customer_id: string | null;
  samsara_driver_id: string | null;
  carrier_id: string | null;
  created_at: string;
}

const LS_KEY = "bb_user_profiles";

const seedUsers: UserProfile[] = [
  {
    id: "profile-internal",
    email: "staff@bullandbearco.com",
    fullName: "Staff Admin",
    role: "internal",
    staffLevel: "admin",
    department: null,
    dispatcherId: null,
    quickmanageCustomerId: "",
    samsaraDriverId: "",
    carrierId: "",
    createdAt: "2026-06-17T00:00:00.000Z",
  },
  {
    id: "profile-dispatch-mgr",
    email: "dispatcher@bullandbearco.com",
    fullName: "Dispatch Manager",
    role: "internal",
    staffLevel: "manager",
    department: "dispatch",
    dispatcherId: null,
    quickmanageCustomerId: "",
    samsaraDriverId: "",
    carrierId: "",
    createdAt: "2026-06-17T00:00:00.000Z",
  },
  {
    id: "profile-dispatch-zack",
    email: "zack.dispatch@bullandbearco.com",
    fullName: "Zack Dispatcher",
    role: "internal",
    staffLevel: "member",
    department: "dispatch",
    dispatcherId: null,
    quickmanageCustomerId: "",
    samsaraDriverId: "",
    carrierId: "",
    createdAt: "2026-06-17T00:00:00.000Z",
  },
  {
    id: "profile-customer",
    email: "customer@example.com",
    fullName: "Preview Customer",
    role: "customer",
    staffLevel: null,
    department: null,
    dispatcherId: null,
    quickmanageCustomerId: "QM-CUST-1001",
    samsaraDriverId: "",
    carrierId: "",
    createdAt: "2026-06-17T00:00:00.000Z",
  },
  {
    id: "profile-driver",
    email: "driver@example.com",
    fullName: "Preview Driver",
    role: "driver",
    staffLevel: null,
    department: null,
    dispatcherId: "profile-dispatch-zack",
    quickmanageCustomerId: "",
    samsaraDriverId: "SAM-DRV-204",
    carrierId: "",
    createdAt: "2026-06-17T00:00:00.000Z",
  },
  {
    id: "profile-carrier",
    email: "owneroperator@example.com",
    fullName: "Preview Owner Operator",
    role: "carrier",
    staffLevel: null,
    department: null,
    dispatcherId: null,
    quickmanageCustomerId: "",
    samsaraDriverId: "",
    carrierId: "CAR-88",
    createdAt: "2026-06-17T00:00:00.000Z",
  },
];

const supabaseReady = () => isSupabaseConfigured && !!supabase;

function rowToProfile(row: ProfileRow): UserProfile {
  return {
    id: row.id,
    email: row.email ?? "",
    fullName: row.full_name ?? "",
    role: row.role,
    staffLevel: row.staff_level ?? null,
    department: row.department ?? null,
    dispatcherId: row.dispatcher_id ?? null,
    quickmanageCustomerId: row.quickmanage_customer_id ?? "",
    samsaraDriverId: row.samsara_driver_id ?? "",
    carrierId: row.carrier_id ?? "",
    createdAt: row.created_at,
  };
}

function localReadAll(): UserProfile[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as UserProfile[];
  } catch {
    /* ignore malformed cache */
  }
  localStorage.setItem(LS_KEY, JSON.stringify(seedUsers));
  return [...seedUsers];
}

function localWriteAll(users: UserProfile[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(users));
}

const byRoleThenEmail = (a: UserProfile, b: UserProfile) =>
  a.role.localeCompare(b.role) || a.email.localeCompare(b.email);

export async function listUserProfiles(): Promise<UserProfile[]> {
  if (supabaseReady()) {
    // Routed through the edge function so admin/manager department scoping is
    // enforced server-side (a manager only sees their own team).
    const { profiles } = await invokeFunction<{ profiles: ProfileRow[] }>("admin-users", {
      action: "list",
    });
    return profiles.map(rowToProfile);
  }
  return localReadAll().sort(byRoleThenEmail);
}

export async function updateUserProfile(profile: UserProfile): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("admin-users", {
      action: "update",
      id: profile.id,
      role: profile.role,
      staffLevel: profile.staffLevel,
      department: profile.department,
      dispatcherId: profile.dispatcherId,
      fullName: profile.fullName,
      email: profile.email,
      quickmanageCustomerId: profile.quickmanageCustomerId,
      samsaraDriverId: profile.samsaraDriverId,
      carrierId: profile.carrierId,
    });
    return;
  }

  localWriteAll(
    localReadAll().map((user) =>
      user.id === profile.id ? { ...profile } : user,
    ),
  );
}

export interface NewUserInput {
  email: string;
  fullName: string;
  role: Role;
  /** Internal staff only. */
  staffLevel?: StaffLevel | null;
  department?: Department | null;
  /** Optional. When blank, the server generates one and returns it once. */
  password?: string;
}

export interface CreatedUser {
  id: string;
  email: string;
  role: Role;
  /** One-time generated password to hand to the new user (null if staff set one). */
  password: string | null;
}

/**
 * Create a real login account. Auth user creation needs the service_role key,
 * so this goes through the staff-only `admin-users` edge function. In preview
 * mode (no Supabase) it just adds a local profile so the UI is demoable.
 */
export async function createUserAccount(input: NewUserInput): Promise<CreatedUser> {
  if (supabaseReady()) {
    const res = await invokeFunction<CreatedUser>("admin-users", {
      action: "create",
      email: input.email.trim().toLowerCase(),
      fullName: input.fullName.trim(),
      role: input.role,
      staffLevel: input.staffLevel ?? null,
      department: input.department ?? null,
      ...(input.password ? { password: input.password } : {}),
    });
    return res;
  }

  const profile: UserProfile = {
    id: `local-${Date.now()}`,
    email: input.email.trim().toLowerCase(),
    fullName: input.fullName.trim(),
    role: input.role,
    staffLevel: input.role === "internal" ? input.staffLevel ?? "member" : null,
    department: input.role === "internal" ? input.department ?? null : null,
    dispatcherId: null,
    quickmanageCustomerId: "",
    samsaraDriverId: "",
    carrierId: "",
    createdAt: new Date().toISOString(),
  };
  localWriteAll([...localReadAll(), profile]);
  return { id: profile.id, email: profile.email, role: profile.role, password: null };
}

/** Delete a login account (staff-only edge function). Cannot delete yourself. */
export async function deleteUserAccount(id: string): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("admin-users", { action: "delete", id });
    return;
  }
  localWriteAll(localReadAll().filter((user) => user.id !== id));
}
